import React from "react";
import ModalWinner from "../component/ModalWinner";
import ModalBidFree from "../component/ModalBidFree";
import ModalPromotion from "../component/ModalPromotion";
import ModalChangePassword from "../component/ModalChangePassword";
import PopupStreakWarning from "../component/PopupStreakWarning";
import PopupRankMaster from "../component/PopupRankMaster";
import PopupIntro from "../page/components/MiniGame/components/Popup/PopupIntro";
import { getDataApi } from "../config/service";
import API_PATH from "../config/API_PATH";
import { POPUP_RULES, POPUP_RULE_BY_ID } from "./popupRules";
import ComponentModalAdvertisement, {
  pickTodayAd,
  isAdSeenToday,
} from "../component/ComponentModalAdvertisement";

// Dấu "đã xem" popup chúc mừng Shwe Master (pu10) của lần thăng hạng hiện tại.
// Ghi ngay khi popup hiện; xoá khi BE tắt cờ show_congrats_popup để lần thăng
// hạng sau lại hiện được. Tiền tố pm_ để đi cùng các key khác của popup manager.
const rankMasterSeenKey = (userId) => `pm_rank_master_seen_${userId}`;

// ═══════════════════════════════════════════════════════════════════════════
// POPUP REGISTRY — phần "hành vi" của mỗi popup: điều kiện đối tượng hiển thị
// (isEligible) và cách render. Phần metadata (priority/frequency/scope/enabled)
// nằm ở popupRules.js, ghép vào theo `id` ở cuối file.
//
// ModalRender.jsx chỉ duyệt danh sách này — không hardcode popup nào cả.
// ═══════════════════════════════════════════════════════════════════════════

// Popup CMS còn trong thời gian diễn ra sự kiện (start_time ≤ now ≤ end_time).
// Thiếu hoặc sai định dạng mốc nào thì bỏ qua mốc đó, không loại popup.
export function isEventRunning(ad, now = Date.now()) {
  const start = Date.parse(ad?.start_time);
  const end = Date.parse(ad?.end_time);
  if (!Number.isNaN(start) && now < start) return false;
  if (!Number.isNaN(end) && now > end) return false;
  return true;
}

// ─── Nguồn data async cho quyết định hiển thị ────────────────────────────────
// Popup khai báo `needs: ['<key>']`, engine chỉ fetch những key mà popup CÒN
// SỐNG ở lượt đó thật sự cần — popup bị tắt hoặc đã hết hạn mức tần suất thì
// không tốn API nào.
export const DATA_SOURCES = {
  // Kết quả đấu giá gần nhất — dùng cho PU2 (non-winner).
  winnerResult: async () => {
    const res = await getDataApi(API_PATH.resultsProduct);
    return res?.success && Array.isArray(res?.data) && res.data.length > 0
      ? res.data[0]
      : null;
  },
  // Trạng thái chuỗi đăng nhập — dùng cho pu9 (cảnh báo mất chuỗi).
  loginStreak: async () => {
    const res = await getDataApi(API_PATH.loginStreakStatus);
    return res?.success ? res.data : null;
  },
  // Trạng thái hạng — dùng cho pu10 (chúc mừng thăng hạng Shwe Master).
  rankStatus: async () => {
    const res = await getDataApi(API_PATH.userRankStatus);
    return res?.success ? res.data : null;
  },
  // Danh sách quảng cáo CMS (đã ORDER BY POSITION ASC) — dùng cho pu6.
  // getDataApi không ném lỗi mà trả object lỗi, nên chỉ cần check data là mảng.
  // Lọc lại theo thời gian diễn ra sự kiện ở FE: BE chỉ lọc START_TIME/END_TIME
  // lúc nạp cache Redis 1 giờ (product.js getListPopup), nên popup đã hết hạn có
  // thể vẫn nằm trong danh sách tới 1 giờ sau END_TIME.
  advertisement: async () => {
    const res = await getDataApi(API_PATH.advertisement);
    if (!Array.isArray(res?.data)) return null;
    const now = Date.now();
    const running = res.data.filter((ad) => isEventRunning(ad, now));
    return running.length > 0 ? running : null;
  },
};

// ─── Hành vi từng popup ──────────────────────────────────────────────────────
//   needs      string[] — key trong DATA_SOURCES
//   fetchOnEveryLoad boolean — true = engine vẫn fetch `needs` của popup này ở
//              mỗi lần tải trang kể cả khi global gate (1 popup/page load,
//              cooldown, login giữa chừng, giới hạn login/ngày) đang chặn.
//              Mặc định engine bỏ qua fetch khi gate chặn để tiết kiệm API.
//   isEligible (ctx) => boolean — điều kiện ĐỐI TƯỢNG hiển thị theo spec.
//              ctx = { userInfo, userId, miniGameConfig, typeIntroTour, data,
//                      pathname, isTourCompleted }
//              KHÔNG kiểm tra tần suất ở đây, engine lo phần đó.
//   render     ({ data, userInfo, userId, dispatch, close, skip, markShown })
//              close()     — user đóng popup: ghi nhận tần suất + bật cooldown
//              skip()      — popup tự thấy không có gì để hiện: KHÔNG tính là
//                            một lần hiển thị, engine chạy tiếp popup kế tiếp
//              markShown() — popup xác nhận đã hiện thật (dùng khi popup tự
//                            quyết định nội dung ở runtime, vd PU6)
const POPUP_BEHAVIOR = {
  // ── pu5 — popup event do FE tự dựng (đang comment) ────────────────────────
  // Vai trò "Mời tham gia CTKM" priority 1 hiện do pu6 (popup CMS) đảm nhận.
  // Popup này do FE tự kiểm soát bằng cờ POPUP_ENABLED.pu5 (popupRules.js).
  // Mỗi event là một popup riêng được tạo mới ở FE, nên khi có event:
  //   1. Import component popup của event vào file này.
  //   2. Đổi `render` bên dưới sang component đó.
  //   3. Bật POPUP_ENABLED.pu5 = true.
  // Hết event thì tắt cờ về false. Không có điều kiện đối tượng nào khác.
  // LƯU Ý: pu5 đứng đầu và luôn eligible nên khi bật cờ, nó sẽ thắng mọi popup
  // khác ở lượt đầu tiên trong ngày.
  // pu5: {
  //   needs: [],
  //   isEligible: () => true,
  //   // Tạm dùng ModalPromotion cho tới khi có event thật.
  //   render: ({ close }) => <ModalPromotion open={true} onCancel={close} />,
  // },

  // ── Priority 2 — Đổi mật khẩu ─────────────────────────────────────────────
  // Trước đây popup này render riêng ở LayoutApp.jsx (ngoài hàng đợi) và chặn
  // bằng cookie hidden-popup-change-pw 24h. Nay vào hàng đợi, tần suất tuần do
  // popupManager quản lý — cookie không còn được đọc để quyết định.
  pu8: {
    needs: [],
    // LƯU Ý: is_updated_password nghĩa là "chưa ĐỔI mật khẩu", không hoàn toàn
    // trùng "chưa TỪNG SET mật khẩu" trong spec. Với luồng đăng nhập OTP hiện
    // tại hai điều này gần như đồng nhất; nếu BE bổ sung has_password thì đổi
    // vế dưới sang !userInfo.has_password.
    // isTourCompleted: giữ nguyên điều kiện cũ của useShowPopup() — không chen
    // popup vào khi guide tour onboarding chưa xong.
    // Không cần chặn theo trang: popup chỉ được xét ở Home (spec mục 4).
    isEligible: ({ userInfo, isTourCompleted }) =>
      !!userInfo &&
      !userInfo.is_updated_password &&
      !!isTourCompleted,
    render: ({ close }) => <ModalChangePassword onClose={close} />,
  },

  // ── Priority 3 — Cảnh báo mất chuỗi (POP-01 / POP-02) ─────────────────────
  // Trước đây Home/index.jsx tự bật DontLoseYourStreak khi day_off > 0 (ngoài
  // hàng đợi, chặn 1 lần/ngày bằng key reviveStreakPopupShownDate). Nay vào
  // hàng đợi; component gói luôn luồng hồi sinh (StreakRestored / OutOfTurns).
  // day_off: 1 = POP-01, 2 = POP-02. Từ 3 trở lên = đã mất chuỗi, không nhắc.
  pu9: {
    needs: ["loginStreak"],
    isEligible: ({ userInfo, data }) =>
      !!userInfo && [1, 2].includes(+data?.loginStreak?.day_off),
    render: ({ data, close }) => (
      <PopupStreakWarning streak={data.loginStreak} onClose={close} />
    ),
  },

  // ── Priority 4 — Tặng Free Turn trong 7 ngày đầu ──────────────────────────
  pu3: {
    needs: [],
    // Cả 2 field do /front/user/current trả về (api/src/controllers/front/user.js
    // currentUser). Chỉ cần 1 trong 2 là popup được chọn:
    //   - isReceivedFreeBidToday: true đúng ngày user được cấp Free Turn (gói
    //     FREE7 có reg_time là hôm nay).
    //   - freeBidCount: số gói FREE7 còn hiệu lực (status != 0 hoặc max_turn != 0)
    //     — tức còn Free Turn chưa dùng. isReceivedFreeBidToday = false nhưng
    //     freeBidCount > 0 thì popup VẪN được chọn, để nhắc user dùng lượt còn lại.
    // Tần suất daily vẫn áp dụng: còn lượt chưa dùng thì mỗi ngày nhắc 1 lần.
    isEligible: ({ userInfo }) =>
      !!userInfo?.isReceivedFreeBidToday || Number(userInfo?.freeBidCount) > 0,
    // BE chỉ điền `message` khi isReceivedFreeBidToday = true — trường hợp chỉ có
    // freeBidCount > 0 thì ModalBidFree tự dựng câu từ freeBidCount.
    render: ({ userInfo, close }) => (
      <ModalBidFree
        open={true}
        message={userInfo?.message}
        freeBidCount={userInfo?.freeBidCount}
        onClose={close}
      />
    ),
  },

  // ── Priority 5 — Chúc mừng thăng hạng Shwe Master ─────────────────────────
  // Trước đây Home/index.jsx tự bật Congratulation khi show_congrats_popup.
  // "Đúng 1 lần mỗi lần thăng hạng": cờ của BE bật suốt lần thăng hạng đó, FE
  // ghi dấu đã xem (rankMasterSeenKey) ngay khi popup hiện để không lặp lại, và
  // xoá dấu khi BE tắt cờ để lần thăng hạng sau hiện lại được.
  pu10: {
    needs: ["rankStatus"],
    isEligible: ({ userInfo, userId, data }) => {
      const rank = data?.rankStatus;
      if (!userInfo || !rank) return false;
      if (!rank.show_congrats_popup) {
        localStorage.removeItem(rankMasterSeenKey(userId));
        return false;
      }
      // Tụt hạng trước khi xem thì huỷ luôn popup.
      return (
        rank.rank?.toUpperCase() === "MASTER" &&
        localStorage.getItem(rankMasterSeenKey(userId)) !== "true"
      );
    },
    render: ({ userId, close }) => (
      <PopupRankMaster
        onShown={() => localStorage.setItem(rankMasterSeenKey(userId), "true")}
        onClose={close}
      />
    ),
  },

  // ── Priority 6 — Winner tuần / Non-winner ─────────────────────────────────
  pu2: {
    needs: ["winnerResult"],
    // winnerResult = đợt đấu giá kết thúc gần nhất (/front/product/results).
    //   status 5 = đợt KHÔNG có winner (tab "no winner" ở page/Result.jsx) →
    //   không có ai để giới thiệu, bỏ qua.
    //   id = WEB_USER.id của winner (isdn đã bị mask) — trùng userInfo.id thì
    //   user CHÍNH LÀ winner. Spec BA: "User không thuộc nhóm winner của tuần".
    // Guest: mọi guest (không có userInfo nên không bị loại).
    isEligible: ({ data, userInfo }) => {
      const r = data?.winnerResult;
      if (!r || +r.status === 5) return false;
      const isWinner = !!userInfo?.id && r.id != null && String(r.id) === String(userInfo.id);
      return !isWinner;
    },
    render: ({ data, close }) => (
      <ModalWinner
        result={data.winnerResult}
        isWinner={false}
        open={true}
        onCancel={close}
      />
    ),
  },

  // ── Priority 7 — Mời tham gia Minigame ────────────────────────────────────
  pu7: {
    needs: [],
    // is_show từ /front/mini-game/get-mini-game (đã có sẵn trong redux, LayoutApp
    // dispatch getConfigMiniGame lúc khởi động).
    // Spec: mọi user đã login, KỂ CẢ user đã chơi Minigame trong ngày; guest thì
    // mọi guest. Không cần chặn theo trang: popup chỉ được xét ở Home.
    isEligible: ({ miniGameConfig }) => !!miniGameConfig?.is_show,
    render: ({ close }) => <PopupIntro open={true} onCancel={close} />,
  },


  // Popup mời bắt đầu tour (trước đây pu4) đã tách khỏi popup manager —
  // xem component/NewUserTourInvite.jsx.

  // ── Priority 1 — Mời tham gia CTKM (CMS Promotional Popup) ────────────────
  // Spec BA: tất cả user login vào dịch vụ trong thời gian diễn ra sự kiện, và
  // mọi guest. "Thời gian diễn ra sự kiện" = start_time..end_time của popup trên
  // CMS (DATA_SOURCES.advertisement lọc lại theo mốc này).
  // Cho cả user đã login và guest (popupRules.js: guest: true). Hạn mức
  // "1 lần/ngày" tính theo thiết bị qua pm_adv_seen — đúng với guest
  // ("1 lần/ngày/thiết bị") và user đã login.
  // Trước đây ComponentModalAdvertisement tự gọi /front/product/popup sau khi
  // được chọn (isEligible luôn true) và hạn mức daily ghi theo "đã được chọn":
  // hiện 1 lần là cả ngày engine loại pu6 khỏi ứng viên, API không được gọi lại
  // khi reload, CMS đổi quảng cáo trong ngày cũng không có tác dụng.
  // Nay engine fetch API ở MỖI page load (needs), popup eligible khi quảng cáo
  // của hôm nay (xoay vòng theo position, mỗi ngày tiến 1 vị trí) CHƯA được xem
  // hôm nay. Hạn mức engine để 'always' (popupRules.js) — tần suất "1 lần/ngày
  // cho mỗi quảng cáo" do isAdSeenToday quyết định. Đã xem rồi thì pu6 không
  // eligible và engine chạy tiếp xuống popup kế tiếp.
  pu6: {
    needs: ["advertisement"],
    // Gọi lại API ở MỌI lần tải trang, kể cả khi đang cooldown / đã có popup
    // hiện / vừa login giữa chừng — để dữ liệu CMS luôn là bản mới nhất.
    fetchOnEveryLoad: true,
    isEligible: ({ data }) => {
      const pick = pickTodayAd(data?.advertisement);
      return !!pick && !isAdSeenToday(pick.ad);
    },
    render: ({ data, close }) => (
      <ComponentModalAdvertisement
        pick={pickTodayAd(data.advertisement)}
        onClose={close}
      />
    ),
  },
};

// ─── Ghép rules + behavior ───────────────────────────────────────────────────
// Quên khai báo 1 bên sẽ ném lỗi ngay lúc load module thay vì âm thầm bỏ qua
// popup (hoặc tệ hơn: hiển thị popup không có giới hạn tần suất).
POPUP_RULES.forEach((rule) => {
  if (!POPUP_BEHAVIOR[rule.id]) {
    throw new Error(
      `[popupRegistry] popup "${rule.id}" có trong popupRules.js nhưng thiếu isEligible/render trong POPUP_BEHAVIOR`,
    );
  }
});
Object.keys(POPUP_BEHAVIOR).forEach((id) => {
  if (!POPUP_RULE_BY_ID.has(id)) {
    throw new Error(
      `[popupRegistry] popup "${id}" có trong POPUP_BEHAVIOR nhưng thiếu khai báo priority/frequency trong popupRules.js`,
    );
  }
});

// Danh sách cuối cùng, đã sắp theo priority — engine chỉ cần duyệt tuần tự.
export const POPUP_REGISTRY = POPUP_RULES.map((rule) => ({
  ...rule,
  ...POPUP_BEHAVIOR[rule.id],
})).sort((a, b) => a.priority - b.priority);
